import { MODERNIZATION_MAX_YEAR } from "../configs/Modernization";
import { columnsAPINamesEnums } from "../enums/Modernization/financialDashboard";
import { getDefaultLoanRepaymentTime, getSingleMeasureFinancialValuesTimeSeries } from "../functions/Modernization";
import { sumByKeys } from "./dataManipulation";
import { aggregatedSumValue } from "./dynamicDashboard";

const FINANCIAL_SAVINGS_KEYS = ["energySavings", "otherSavings", "subsidies"];
const FINANCIAL_COSTS_KEYS = [
  "maintenanceCosts",
  "servicesCosts",
  "ownCapital",
  "foreignCapitalCost",
  "subsidies",
  "loanRepayment",
];

export const calculateInitialEnergySaving = (heatEnergyCostSavings, electricityCostSavings) => {
  return Number(heatEnergyCostSavings ?? 0) + Number(electricityCostSavings ?? 0);
};

const getFinancialSavingsFromRow = (row = {}) => {
  return FINANCIAL_SAVINGS_KEYS.reduce((total, key) => total + Number(row?.[key] ?? 0), 0);
};

const getFinancialCostsFromRow = (row = {}) => {
  return FINANCIAL_COSTS_KEYS.reduce((total, key) => total + Math.abs(Number(row?.[key] ?? 0)), 0);
};

export const getFinancialTotalsForRow = (row = {}) => {
  return {
    totalCosts: getFinancialCostsFromRow(row),
    totalSavings: getFinancialSavingsFromRow(row),
  };
};

export const getFinancialTotalsTimeline = (data = []) => {
  return data.map((row) => ({
    ...getFinancialTotalsForRow(row),
    year: row?.year,
  }));
};

export const getAggregatedFinancialTotals = (data = []) => {
  return getFinancialTotalsTimeline(data).reduce(
    (total, row) => ({
      totalCosts: total.totalCosts + Number(row?.totalCosts ?? 0),
      totalSavings: total.totalSavings + Number(row?.totalSavings ?? 0),
    }),
    { totalCosts: 0, totalSavings: 0 }
  );
};

export const getFinancialPartitionDiagrams = ({ contributorMeta = {}, defaultCostsColor, defaultSavingsColor }) => {
  return [
    ...FINANCIAL_SAVINGS_KEYS.map((key) => ({
      color: contributorMeta[key]?.color ?? defaultSavingsColor,
      dataKey: key,
      group: "savings",
      name: contributorMeta[key]?.name ?? key,
    })),
    ...FINANCIAL_COSTS_KEYS.map((key) => ({
      color: contributorMeta[key]?.color ?? defaultCostsColor,
      dataKey: key,
      group: "costs",
      name: contributorMeta[key]?.name ?? key,
    })),
  ];
};

export const getAggregatedFinancialContributors = ({ data = [], partitionDiagrams = [] }) => {
  return partitionDiagrams.map((diagram) => {
    const value = data.reduce((total, row) => {
      if (diagram.group === "costs") {
        return total + Math.abs(Number(row?.[diagram.dataKey] ?? 0));
      }
      return total + Number(row?.[diagram.dataKey] ?? 0);
    }, 0);

    return {
      ...diagram,
      value,
    };
  });
};

export const getFinancialDataTimeSeries = ({ maxYear, minYear, selectedMeaures }) => {
  const defaultLoanRepaymentTime = getDefaultLoanRepaymentTime();
  const allSelectedMeauresRichedTimeSeries = selectedMeaures?.map((selectedMeaure) =>
    getSingleMeasureFinancialValuesTimeSeries({
      endYear: MODERNIZATION_MAX_YEAR,
      initialValues: {
        foreignCapital: selectedMeaure[columnsAPINamesEnums.FOEIGN_CAPITAL],
        inflationRatePercentage: selectedMeaure[columnsAPINamesEnums.INFLATION_RATE],
        initialEnergySaving: calculateInitialEnergySaving(
          selectedMeaure[columnsAPINamesEnums.HEAT_ENERGY_COST_SAVINGS],
          selectedMeaure[columnsAPINamesEnums.ELECTRICITY_COST_SAVINGS]
        ),
        initialMaintenanceCosts: selectedMeaure[columnsAPINamesEnums.MAINTENANCE_COST],
        initialOtherSavings: selectedMeaure[columnsAPINamesEnums.OTHER_COST_SAVINGS],
        initialServicesCosts: selectedMeaure[columnsAPINamesEnums.SERVICES_COST],
        interestRatePercentage: selectedMeaure[columnsAPINamesEnums.INTEREST_RATE],
        investments: selectedMeaure[columnsAPINamesEnums.INVESTMENTS],
        loanRepaymentTime: selectedMeaure[columnsAPINamesEnums.REPAYMENT_TIME] ?? defaultLoanRepaymentTime,
        ownCapital: selectedMeaure[columnsAPINamesEnums.OWN_CAPITAL],
        subsidies: selectedMeaure[columnsAPINamesEnums.SUBSIDIES],
      },
      startYear: selectedMeaure.year,
    })
  );

  const aggregateKeys = [
    "energySavings",
    "foreignCapitalCost",
    "loanRepayment",
    "maintenanceCosts",
    "otherSavings",
    "servicesCosts",
    "foreignCapital",
    "ownCapital",
    "subsidies",
    "cashFlow",
    "investments",
  ];
  const SelectedMeasuresTimeSeriesAggregated = aggregatedSumValue({
    aggregateColumns: aggregateKeys,
    data: allSelectedMeauresRichedTimeSeries?.reduce((total, current) => [...total, ...current], []),
    groupByColumns: ["year"],
  });

  const dataForChart = [];
  for (let currentYear = minYear; currentYear <= maxYear; currentYear++) {
    const currentYearData = SelectedMeasuresTimeSeriesAggregated?.find((yearData) => yearData.year === currentYear);
    if (currentYearData) {
      dataForChart.push(currentYearData);
    } else {
      dataForChart.push({ year: currentYear });
    }
  }

  const totalSavings = sumByKeys({ data: dataForChart, keys: FINANCIAL_SAVINGS_KEYS });
  const totalCostsRaw = sumByKeys({ data: dataForChart, keys: FINANCIAL_COSTS_KEYS });
  const totalSavingsValue = getFinancialSavingsFromRow(totalSavings);
  const totalCostsValue = getFinancialCostsFromRow(totalCostsRaw);

  return {
    dataForChart,
    financialSummary: {
      savingsMinusCosts: totalSavingsValue - totalCostsValue,
      totalCostsValue,
      totalSavingsValue,
    },
  };
};

export const convertValueBGFToNGF = (value, BGF_NGF_Ratio) => {
  return value * BGF_NGF_Ratio;
};
